/**
 * The Operations endpoints answer 503 (+ Retry-After) while the API's background warmer builds the
 * 200-day request and task-action caches, typically for the first minutes after an API start. The
 * screen treats that as "warming" (keep waiting, retry) rather than an error.
 */
export function isSnapshotWarming(error: unknown): boolean {
  return (error as { status?: number } | null | undefined)?.status === 503;
}

/** Which cache the API said it is still building (the 503 body's `reason`). */
export type WarmingReason = "requests" | "activity";

/**
 * Reads the step out of the 503 body. `apiFetch` keeps the raw response text in `message`, so the
 * body is parsed here; anything unexpected yields null and the card falls back to generic wording.
 */
export function warmingReason(error: unknown): WarmingReason | null {
  const message = (error as { message?: unknown } | null | undefined)?.message;
  if (typeof message !== "string") return null;
  try {
    const body = JSON.parse(message) as { reason?: unknown };
    const reason = typeof body.reason === "string" ? body.reason.toLowerCase() : "";
    return reason === "requests" || reason === "activity" ? reason : null;
  } catch {
    return null;
  }
}

/** End-user wording for each warming step, in the order the warmer runs them. */
export const WARMING_STEP_LABEL: Record<WarmingReason, string> = {
  requests: "Step 1 of 2: loading 200 days of requests",
  activity: "Step 2 of 2: indexing task activity",
};

/** How many 10-second retries before giving up and showing the error card (≈15 minutes). */
export const WARMING_MAX_RETRIES = 90;
export const WARMING_RETRY_DELAY_MS = 10_000;
/** After this long in the warming state the card adds a hint on where to look on the server. */
export const WARMING_SLOW_AFTER_MS = 3 * 60_000;

export function operationsRetry(failureCount: number, error: unknown): boolean {
  return isSnapshotWarming(error) ? failureCount < WARMING_MAX_RETRIES : failureCount < 1;
}

export function operationsRetryDelay(_failureCount: number, error: unknown): number {
  return isSnapshotWarming(error) ? WARMING_RETRY_DELAY_MS : 1_000;
}

/** "45 s", "2 min 10 s": how long the screen has been waiting, for the warming card. */
export function formatWaiting(ms: number): string {
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return minutes === 0 ? `${seconds} s` : `${minutes} min ${seconds} s`;
}
